import type { StorageAdapter, StoragePayload } from './types';

/**
 * Persists app state to a remote REST API.
 *
 * API contract (see API_SPEC.md for full documentation):
 *   GET    /api/health              → { status: 'ok' }
 *   GET    /api/v1/sessions/:id     → StoragePayload | 404
 *   PUT    /api/v1/sessions/:id     → { ok: true }
 *   DELETE /api/v1/sessions/:id     → { ok: true }
 *
 * Environment variables (set in .env.local):
 *   VITE_API_BASE_URL  – required, e.g. https://api.example.com
 *   VITE_API_TOKEN     – optional Bearer token
 */
export class RemoteApiAdapter implements StorageAdapter {
  readonly isRemote = true;

  private readonly baseUrl: string;
  private readonly token: string | undefined;
  private readonly deviceId: string;

  constructor(baseUrl: string, token: string | undefined, deviceId: string) {
    // Strip trailing slash to keep URLs consistent
    this.baseUrl = baseUrl.replace(/\/+$/, '');
    this.token = token;
    this.deviceId = deviceId;
  }

  // Build common fetch options with auth header when a token is configured
  private fetchOptions(method: string, body?: unknown): RequestInit {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;

    return {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    };
  }

  private get sessionUrl(): string {
    return `${this.baseUrl}/api/v1/sessions/${encodeURIComponent(this.deviceId)}`;
  }

  async load(): Promise<StoragePayload | null> {
    try {
      const res = await fetch(this.sessionUrl, this.fetchOptions('GET'));
      if (res.status === 404) return null;
      if (!res.ok) {
        console.warn(`[RemoteApiAdapter] load failed: HTTP ${res.status}`);
        return null;
      }
      const data = await res.json();
      // Ensure the shape matches StoragePayload (defensive)
      return {
        name: data.name ?? '',
        records: data.records ?? {},
        step: data.step ?? '',
        checklistId: data.checklistId ?? null,
        committeeComments: data.committeeComments ?? {},
      };
    } catch (err) {
      console.warn('[RemoteApiAdapter] load error:', err);
      return null;
    }
  }

  async save(payload: StoragePayload): Promise<void> {
    try {
      const res = await fetch(
        this.sessionUrl,
        this.fetchOptions('PUT', payload)
      );
      if (!res.ok) {
        console.warn(`[RemoteApiAdapter] save failed: HTTP ${res.status}`);
      }
    } catch (err) {
      console.warn('[RemoteApiAdapter] save error:', err);
    }
  }

  async clear(): Promise<void> {
    try {
      const res = await fetch(this.sessionUrl, this.fetchOptions('DELETE'));
      if (!res.ok) {
        console.warn(`[RemoteApiAdapter] clear failed: HTTP ${res.status}`);
      }
    } catch (err) {
      console.warn('[RemoteApiAdapter] clear error:', err);
    }
  }

  /**
   * Static helper: probe the API health endpoint.
   * Returns true when the server is reachable and returns HTTP 200.
   */
  static async checkHealth(baseUrl: string, token?: string): Promise<boolean> {
    try {
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${baseUrl.replace(/\/+$/, '')}/api/health`, {
        method: 'GET',
        headers,
        // Short timeout to avoid blocking startup for too long
        signal: AbortSignal.timeout(5000),
      });
      return res.ok;
    } catch {
      return false;
    }
  }
}
